import { CreateAttempt } from '@/common/types/session';
import { playSentenceAudio, soundOutClozeSentence } from '@/common/utils/audio';
import { sanitize } from '@/common/utils/helpers';
import trackMixpanelEvent from '@/common/utils/mixpanel';
import { EventBus } from '@/game/GameComponents/PhaserGame/EventBus';
import {
  GameInstance,
  getGeneralGameState,
  getSession,
  getStudent,
  getWordRifts,
} from '@/game/GameComponents/PhaserGame/GameInstance';
import EventsManager from '@/game/GameComponents/PhaserGame/Managers/EventsManager';
import { MinigameController } from '@/game/GameComponents/PhaserGame/MinigameControllers/MinigameController';
import { GeneralGameState } from '@/game/GameComponents/PhaserGame/Types/IGeneralGameState';
import { WordRiftsState } from '@/game/GameComponents/PhaserGame/Types/IWordRiftsState';
import GAME_EVENTS from '@/game/GameComponents/PhaserGame/Utils/gameEventsDef';
import { waitForSeconds } from '@/game/GameComponents/PhaserGame/Utils/PhaserUtils';
import { calculateProgress } from '@/game/gameHelpers';
import MinigameCardSlot from '@/game/prefabs/StudentGame/MinigameCardSlot';
import StudentGameCard from '@/game/prefabs/StudentGame/StudentGameCard';
import Minigame from '@/game/scenes/Minigame';
import { v4 as uuidv4 } from 'uuid';
import { CardFeedback } from '../Types/MinigameTypes';

export class WordRiftsController extends MinigameController {
  constructor(minigame: Minigame) {
    super(minigame);
    EventsManager.registerEventOn(
      minigame,
      GAME_EVENTS.MINIGAME_CHECK_ANSWER,
      () => {
        this.abortReadoutController.abort();
      },
      this,
    );
  }

  private abortReadoutController: AbortController = new AbortController();

  override canSubmitAnswer(): boolean {
    const userSubmittedCard = this.minigame
      .getBoardCards()
      .find((card) => card.isLocked === false);
    return !!userSubmittedCard;
  }

  override startRound() {
    const isRoundEnded = getGeneralGameState().isRoundEnded;
    if (isRoundEnded) {
      GameInstance.instance.updateGeneralGameState({
        isRoundEnded: false,
      });
    }
    this.handleWordRiftsDeal();
  }

  async handleWordRiftsDeal() {
    const generalGameState: GeneralGameState = getGeneralGameState();
    const wordRiftsState: WordRiftsState = getWordRifts();
    const currentWordIndex = wordRiftsState.usedWords.length;

    const words = generalGameState.gameData.wordRifts;

    if (!words) {
      console.error('No words found in word rifts data.');
      return;
    }

    const currentWord = words[currentWordIndex];

    if (!currentWord.example) {
      console.error('No example sentence found for word set:', currentWord);
      return;
    }

    // Enhanced punctuation handling
    const punctuation = ['.', '?', '!', ',', ':', ';'];
    const endsWithPunctuation = punctuation.some((p) =>
      currentWord.example?.endsWith(p),
    );

    // Store the ending punctuation if present
    const endPunctuation =
      endsWithPunctuation && currentWord.example
        ? currentWord.example.charAt(currentWord.example.length - 1)
        : '';

    // Split sentence and preserve any mid-sentence punctuation
    const sentenceToBuild = currentWord.example
      .slice(0, endsWithPunctuation ? -1 : undefined)
      .split(' ');

    let missingWord = sentenceToBuild.find(
      (word: string) => sanitize(word) === sanitize(currentWord.spelling),
    );

    if (!missingWord) {
      console.error('Missing word not found in sentence:', currentWord);
      return;
    }

    // Handle punctuation attached to missing word
    if (missingWord) {
      const hasPunctuation = punctuation.some((p) => missingWord!.endsWith(p));
      if (hasPunctuation) {
        const wordPunctuation = missingWord.charAt(missingWord.length - 1);
        missingWord = missingWord.slice(0, -1);
        // Reattach punctuation to the word in sentenceToBuild
        const missingWordIndex = sentenceToBuild.findIndex(
          (word) => word === missingWord + wordPunctuation,
        );
        if (missingWordIndex !== -1) {
          sentenceToBuild[missingWordIndex] = missingWord;
        }
      }
    }

    const options = words
      .map((wordSet) => sanitize(wordSet.spelling))
      .filter((word: string) => word !== sanitize(currentWord.spelling))
      .sort(() => Math.random() - 0.5);

    const dealtWords = [missingWord, ...options]
      .slice(0, 4)
      .sort(() => Math.random() - 0.5);

    // Rebuild final sentence with all punctuation
    const finalSentenceToBuild = [...sentenceToBuild];
    if (endPunctuation) {
      if (
        finalSentenceToBuild[finalSentenceToBuild.length - 1] !== missingWord
      ) {
        finalSentenceToBuild[finalSentenceToBuild.length - 1] += endPunctuation;
      } else {
        finalSentenceToBuild.push(endPunctuation);
      }
    }

    GameInstance.instance.updateWordRifts({
      sentenceToBuild: finalSentenceToBuild,
      missingWord,
      dealtWords,
      currentWordId: currentWord.id,
    });
  }

  handleReplayWordRiftsThunk() {}

  override async playAudioForRound() {
    if (getStudent()?.settings?.wordSegmentationEnabled) {
      await this.playInstructions();
    }
  }

  override async checkMinigameAnswer() {
    const student = getStudent();
    const generalGameState = getGeneralGameState();
    const wordRiftsState = getWordRifts();

    const currentActivityId = getSession().currentActivity?.id;
    if (!currentActivityId) {
      console.error('No current activity ID found');
      return;
    }

    const cards = this.minigame.getBoardCards();
    if (!cards) {
      console.error('No board state found');
      return;
    }

    const studentAttemptCard = cards.find(
      (card) => card.isLocked === false,
    ) as StudentGameCard;
    if (!studentAttemptCard) {
      console.error('No student attempt card found');
      return;
    }

    const studentAttempt = sanitize(
      studentAttemptCard.cardData?.spelling ?? '',
    );
    if (!studentAttempt) {
      console.error('No student attempt found');
      return;
    }

    // sentence to build with missing word replaced by '_____'
    const attemptContext = wordRiftsState.sentenceToBuild
      .map((word) => (word === wordRiftsState.missingWord ? '_____' : word))
      .join(' ');

    const attemptData: CreateAttempt = {
      organisationId: student.organisationId,
      studentId: student.id,
      activityId: currentActivityId,
      activityName: 'wordrifts',
      context: attemptContext,
      correctAnswer: wordRiftsState.missingWord,
      correctAnswerId: wordRiftsState.currentWordId,
      studentAnswer: studentAttempt,
      incorrectAnswers: wordRiftsState.dealtWords,
      cardsPlayed: cards
        .filter((card) => !card.isLocked)
        .map((card) => card.cardText),
      control: this.minigame.getControlScheme(student.settings.controllerType),
      startedAt: getSession().currentAttemptStartedAt,
      endedAt: Date.now(),
    };
    GameInstance.instance.addAttempt(attemptData);

    const isCorrect =
      sanitize(studentAttempt) === sanitize(wordRiftsState.missingWord);

    // update accuracy
    GameInstance.instance.updateActivityAccuracy(isCorrect);

    if (isCorrect) {
      const currentWordIndex = wordRiftsState.usedWords.length;
      const words = generalGameState.gameData.wordRifts;
      if (!words) {
        console.error('No words found in word rifts data.');
        return;
      }
      const currentWord = words[currentWordIndex].spelling;
      GameInstance.instance.updateWordRifts({
        usedWords: [...wordRiftsState.usedWords, currentWord],
      });

      await this.showFeedback(this.getCardFeedback(), {
        correctAnswer: true,
      });
    } else {
      await this.showFeedback(this.getItemFeedback(), {
        correctAnswer: false,
        forceIndividualFeedback: true,
        forceReadout: true,
      });
    }

    const updatedWordRiftsState = getWordRifts();
    const updatedProgress = calculateProgress(
      generalGameState,
      updatedWordRiftsState,
      100,
    );
    GameInstance.instance.updateGeneralGameState({
      progress: updatedProgress,
    });

    if (isCorrect) {
      EventBus.emit(GAME_EVENTS.MINIGAME_CORRECT_ANSWER, attemptData);
    } else {
      EventBus.emit(GAME_EVENTS.MINIGAME_INCORRECT_ANSWER, attemptData);
    }
  }

  createCardList(minigameScene: Minigame): {
    dealtCards: (StudentGameCard | null)[];
    boardCards: (StudentGameCard | null)[];
  } {
    const cardConfig = {
      dealtCards: getWordRifts().dealtWords.map((spelling: string) => {
        const newCard = new StudentGameCard(minigameScene, 0, 0);
        minigameScene.initializeNewMinigameCard(
          newCard,
          { id: uuidv4(), spelling: sanitize(spelling) },
          'hand',
        );
        return newCard;
      }),
      boardCards: getWordRifts().sentenceToBuild.map((spelling, index) => {
        const missingWord = getWordRifts().missingWord;
        if (sanitize(spelling) === sanitize(missingWord)) {
          return null;
        }
        const newCard = new StudentGameCard(minigameScene, 0, 0);
        minigameScene.initializeNewMinigameCard(
          newCard,
          { id: uuidv4(), spelling },
          'board',
          index,
        );

        if (!newCard) {
          console.error('No new card found');
          return null;
        }

        newCard.lockCard();
        return newCard;
      }),
    };
    this.minigame.getBoardDropSite().layoutGroup?.updateLayout('instant');
    return cardConfig;
  }

  override getMaxBoardCards(): number {
    const wordRifts = getWordRifts();
    return wordRifts.sentenceToBuild.length;
  }

  override async playInstructions() {
    const missingWord = getWordRifts().missingWord;
    const student = getStudent();
    const studentId = student.id;

    if (studentId && missingWord) {
      trackMixpanelEvent('Replay word button clicked', {
        word: missingWord,
      });
    }

    try {
      const audioDelay = student.settings.audio.audioDelay;
      const wordRiftsState: WordRiftsState = getWordRifts();
      const finalSentenceToBuild = wordRiftsState.sentenceToBuild;
      const missingWord = wordRiftsState.missingWord;
      this.abortReadoutController = new AbortController();

      await soundOutClozeSentence(
        finalSentenceToBuild.map(sanitize).join(' ').trim(),
        sanitize(missingWord),
        audioDelay || 0,
        false,
        this.abortReadoutController.signal,
        (_word, index) => {
          const card = this.minigame.getBoardDropSite().getLayoutGroup().cards[
            index
          ];
          if (card instanceof StudentGameCard) {
            card?.showNeutralFeedback();
          }
          if (card instanceof MinigameCardSlot) {
            (card as MinigameCardSlot).showNeutralFeedback();
          }
        },
      );
    } catch (error) {
      console.error('Error during audio playback:', error);
    }
  }

  override getCardFeedback(): CardFeedback[] {
    const boardState = this.minigame.getBoardDropSite().layoutGroup;
    if (!boardState) {
      console.error('No board state found');
      return [];
    }
    const cards = boardState.getMinigameCards() as StudentGameCard[];
    const missingWord = getWordRifts().missingWord;
    return cards.map((card) => ({
      card,
      isCorrect: card.isLocked || card.cardData?.spelling === missingWord,
    }));
  }

  override async readFinalAnswer() {
    const sentenceToBuild = getWordRifts().sentenceToBuild;

    // Combine trailing fullstop with last word.
    if (sentenceToBuild[sentenceToBuild.length - 1] === '.') {
      sentenceToBuild.pop();
      sentenceToBuild[sentenceToBuild.length - 1] += '.';
    }

    if (!sentenceToBuild) return;
    await playSentenceAudio(sentenceToBuild.join(' '));
  }

  override canPlayPhonemeOnCardDrop() {
    return false;
  }

  override canShowArticulatoryGestures() {
    return false;
  }

  override canShowWordSegmentation() {
    return true;
  }

  override async handleIncorrectFeedbackComplete(feedback: CardFeedback[]) {
    this.returnIncorrectCardsToHand(feedback);
    await waitForSeconds(this.minigame.time, 0.25);
    await this.playInstructions();
  }
}
